import type * as SchemaTypes from '../types';

import type { CompanyWithPermissionsContextDataFragment } from './CompanyWithPermissionsContextData';
import type { CompanyContextDataFragment } from './CompanyContextData';
import type { TranslatedStringDataFragment } from './TranslatedStringData';
import type { LanguagePackContextDataFragment } from './LanguagePackContextData';
import type { UploadFileWithPreviewFormDataFragment } from './UploadFileWithPreviewFormData';
import type { AvailableModulesDataFragment } from './AvailableModulesData';
import type { CompanyDealerDataFragment } from './CompanyDealerData';
import type { MaintenanceUpdateFragment } from './MaintenanceUpdate';
import type { SimpleVersioningDataFragment } from './SimpleVersioningData';
import type { AuthorData_CorporateCustomer_Fragment, AuthorData_ExternalBank_Fragment, AuthorData_Guarantor_Fragment, AuthorData_LocalCustomer_Fragment, AuthorData_Salesforce_Fragment, AuthorData_SystemBank_Fragment, AuthorData_User_Fragment } from './AuthorData';
import type { UserAvatarSpecsFragment } from './UserAvatarSpecs';
import type { EdmEmailFooterPublicDataFragment } from './EdmEmailFooterPublicData';
import type { EdmSocialMediaDataFragment } from './EdmSocialMediaData';
import type { RouterLayoutContextData_BasicLayout_Fragment, RouterLayoutContextData_BasicProLayout_Fragment } from './RouterLayoutContextData';
import type { EndpointContextData_ApplicationListEndpoint_Fragment, EndpointContextData_ConfiguratorApplicationEntrypoint_Fragment, EndpointContextData_CustomerListEndpoint_Fragment, EndpointContextData_DummyPrivatePageEndpoint_Fragment, EndpointContextData_DummyWelcomePageEndpoint_Fragment, EndpointContextData_EventApplicationEntrypoint_Fragment, EndpointContextData_FinderApplicationEntrypoint_Fragment, EndpointContextData_FinderApplicationPublicAccessEntrypoint_Fragment, EndpointContextData_MobilityApplicationEntrypoint_Fragment, EndpointContextData_StandardApplicationEntrypoint_Fragment, EndpointContextData_StandardApplicationPublicAccessEntrypoint_Fragment, EndpointContextData_WebPageEndpoint_Fragment } from './EndpointContextData';
import type { DummyPrivatePageEndpointContextDataFragment } from './DummyPrivatePageEndpointContextData';
import type { StandardApplicationEntrypointContextDataFragment } from './StandardApplicationEntrypointContextData';
import type { ApplicationMarketTypeFragment_DefaultApplicationMarket_Fragment, ApplicationMarketTypeFragment_NewZealandApplicationMarket_Fragment, ApplicationMarketTypeFragment_SingaporeApplicationMarket_Fragment } from './ApplicationMarketTypeFragment';
import type { DealerMarketDataFragment } from './DealerMarketData';
import type { BankDealerMarketDataFragment } from './BankDealerMarketData';
import type { NzFeesDealerMarketDataFragment } from './NzFeesDealerMarketData';
import type { AppointmentModuleSpecsFragment } from './AppointmentModuleSpecs';
import type { AppointmentTimeSlotDataFragment } from './AppointmentTimeSlotData';
import type { NamirialSigningModuleSpecsFragment } from './NamirialSigningModuleSpecs';
import type { NamirialSettingsSpecFragment } from './NamirialSettingsSpec';
import type { AppointmentModuleEmailContentsSpecsFragment, AppointmentModuleEmailContentCustomerSpecsFragment, AppointmentModuleEmailContentSpecsFragment, AppointmentModuleEmailContentSalesPersonSpecsFragment, AppointmentModuleEmailContentFinderReservationSpecsFragment } from './AppointmentModuleEmailContentsSpecs';
import type { DealerTranslatedStringSettingDataFragment } from './DealerTranslatedStringData';
import type { InsurerEntrypointContextDataFragment } from './InsurerEntrypointContextData';
import type { DealerDisclaimersConfiguratorDataFragment } from './DealerPriceDisclaimerConfiguratorData';
import type { FlexibleDiscountDataFragment } from './FlexibleDiscountData';
import type { ApplicationListEndpointContextDataFragment } from './ApplicationListEndpointContextData';
import type { EventApplicationEntrypointContextDataFragment } from './EventApplicationEntrypointContextData';
import type { ConfiguratorApplicationEntrypointContextDataFragment } from './ConfiguratorApplicationEntrypointContextData';
import type { MyInfoSettingSpecFragment } from './MyInfoSettingSpec';
import type { CustomerListEndpointContextDataFragment } from './CustomerListEndpointContextData';
import type { MobilityApplicationEntrypointContextDataFragment } from './MobilityApplicationEntrypointContextData';
import type { DealerBookingCodeDataFragment } from './DealerBookingCodeData';
import type { MobilityLocationDataFragment } from './MobilityLocationData';
import type { UserPreviewDataFragment } from './UserPreviewData';
import type { DateUnitDataFragment } from './DateUnitData';
import type { WebpageEndpointContextDataFragment } from './WebpageEndpointContextData';
import type { WebPageEndpointSpecsFragment } from './WebPageEndpointSpecs';
import type { WebPagePathDataFragment } from './WebPagePathData';
import type { StandardApplicationPublicAccessEntrypointContextDataFragment } from './StandardApplicationPublicAccessEntrypointContextData';
import type { DealerApplicationFragmentFragment } from './DealerApplicationFragment';
import type { DealerContactFragmentFragment } from './DealerContactFragment';
import type { DealerSocialMediaFragmentFragment } from './DealerSocialMediaFragment';
import type { DealerDisclaimersFragmentFragment } from './DealerDisclaimersFragment';
import type { DealerIntegrationDetailsFragmentFragment } from './DealerIntegrationDetailsFragment';
import type { FinderApplicationPublicAccessEntrypointContextDataFragment } from './FinderApplicationPublicAccessEntrypointContextData';
import type { EntrypointFinderApplicationPublicModuleFragment } from './EntrypointFinderApplicationPublicModule';
import type { FinderApplicationModuleEmailContentSpecsFragment } from './FinderApplicationModuleEmailContentSpecs';
import type { TranslatedTextDataFragment } from './TranslationTextData';
import type { FinderApplicationEntrypointContextDataFragment } from './FinderApplicationEntrypointContextData';
import type { EntrypointFinderApplicationPrivateModuleFragment } from './EntrypointFinderApplicationPrivateModule';
import type { MenuItemSpecs_MenuCustomPathItem_Fragment, MenuItemSpecs_MenuEndpointItem_Fragment, MenuItemSpecs_MenuLogoutActionItem_Fragment } from './MenuItemSpecs';
import type { TranslatedStringSpecsFragment } from './TranslatedStringSpecs';
import { gql } from '@apollo/client';
import { CompanyWithPermissionsContextDataFragmentDoc } from './CompanyWithPermissionsContextData';
import { CompanyContextDataFragmentDoc } from './CompanyContextData';
import { TranslatedStringDataFragmentDoc } from './TranslatedStringData';
import { LanguagePackContextDataFragmentDoc } from './LanguagePackContextData';
import { UploadFileWithPreviewFormDataFragmentDoc } from './UploadFileWithPreviewFormData';
import { AvailableModulesDataFragmentDoc } from './AvailableModulesData';
import { CompanyDealerDataFragmentDoc } from './CompanyDealerData';
import { MaintenanceUpdateFragmentDoc } from './MaintenanceUpdate';
import { SimpleVersioningDataFragmentDoc } from './SimpleVersioningData';
import { AuthorDataFragmentDoc } from './AuthorData';
import { UserAvatarSpecsFragmentDoc } from './UserAvatarSpecs';
import { EdmEmailFooterPublicDataFragmentDoc } from './EdmEmailFooterPublicData';
import { EdmSocialMediaDataFragmentDoc } from './EdmSocialMediaData';
import { RouterLayoutContextDataFragmentDoc } from './RouterLayoutContextData';
import { EndpointContextDataFragmentDoc } from './EndpointContextData';
import { DummyPrivatePageEndpointContextDataFragmentDoc } from './DummyPrivatePageEndpointContextData';
import { StandardApplicationEntrypointContextDataFragmentDoc } from './StandardApplicationEntrypointContextData';
import { ApplicationMarketTypeFragmentFragmentDoc } from './ApplicationMarketTypeFragment';
import { DealerMarketDataFragmentDoc } from './DealerMarketData';
import { BankDealerMarketDataFragmentDoc } from './BankDealerMarketData';
import { NzFeesDealerMarketDataFragmentDoc } from './NzFeesDealerMarketData';
import { AppointmentModuleSpecsFragmentDoc } from './AppointmentModuleSpecs';
import { AppointmentTimeSlotDataFragmentDoc } from './AppointmentTimeSlotData';
import { NamirialSigningModuleSpecsFragmentDoc } from './NamirialSigningModuleSpecs';
import { NamirialSettingsSpecFragmentDoc } from './NamirialSettingsSpec';
import { AppointmentModuleEmailContentsSpecsFragmentDoc, AppointmentModuleEmailContentCustomerSpecsFragmentDoc, AppointmentModuleEmailContentSpecsFragmentDoc, AppointmentModuleEmailContentSalesPersonSpecsFragmentDoc, AppointmentModuleEmailContentFinderReservationSpecsFragmentDoc } from './AppointmentModuleEmailContentsSpecs';
import { DealerTranslatedStringSettingDataFragmentDoc } from './DealerTranslatedStringData';
import { InsurerEntrypointContextDataFragmentDoc } from './InsurerEntrypointContextData';
import { DealerDisclaimersConfiguratorDataFragmentDoc } from './DealerPriceDisclaimerConfiguratorData';
import { FlexibleDiscountDataFragmentDoc } from './FlexibleDiscountData';
import { ApplicationListEndpointContextDataFragmentDoc } from './ApplicationListEndpointContextData';
import { EventApplicationEntrypointContextDataFragmentDoc } from './EventApplicationEntrypointContextData';
import { ConfiguratorApplicationEntrypointContextDataFragmentDoc } from './ConfiguratorApplicationEntrypointContextData';
import { MyInfoSettingSpecFragmentDoc } from './MyInfoSettingSpec';
import { CustomerListEndpointContextDataFragmentDoc } from './CustomerListEndpointContextData';
import { MobilityApplicationEntrypointContextDataFragmentDoc } from './MobilityApplicationEntrypointContextData';
import { DealerBookingCodeDataFragmentDoc } from './DealerBookingCodeData';
import { MobilityLocationDataFragmentDoc } from './MobilityLocationData';
import { UserPreviewDataFragmentDoc } from './UserPreviewData';
import { DateUnitDataFragmentDoc } from './DateUnitData';
import { WebpageEndpointContextDataFragmentDoc } from './WebpageEndpointContextData';
import { WebPageEndpointSpecsFragmentDoc } from './WebPageEndpointSpecs';
import { WebPagePathDataFragmentDoc } from './WebPagePathData';
import { StandardApplicationPublicAccessEntrypointContextDataFragmentDoc } from './StandardApplicationPublicAccessEntrypointContextData';
import { DealerApplicationFragmentFragmentDoc } from './DealerApplicationFragment';
import { DealerContactFragmentFragmentDoc } from './DealerContactFragment';
import { DealerSocialMediaFragmentFragmentDoc } from './DealerSocialMediaFragment';
import { DealerDisclaimersFragmentFragmentDoc } from './DealerDisclaimersFragment';
import { DealerIntegrationDetailsFragmentFragmentDoc } from './DealerIntegrationDetailsFragment';
import { FinderApplicationPublicAccessEntrypointContextDataFragmentDoc } from './FinderApplicationPublicAccessEntrypointContextData';
import { EntrypointFinderApplicationPublicModuleFragmentDoc } from './EntrypointFinderApplicationPublicModule';
import { FinderApplicationModuleEmailContentSpecsFragmentDoc } from './FinderApplicationModuleEmailContentSpecs';
import { TranslatedTextDataFragmentDoc } from './TranslationTextData';
import { FinderApplicationEntrypointContextDataFragmentDoc } from './FinderApplicationEntrypointContextData';
import { EntrypointFinderApplicationPrivateModuleFragmentDoc } from './EntrypointFinderApplicationPrivateModule';
import { MenuItemSpecsFragmentDoc } from './MenuItemSpecs';
import { TranslatedStringSpecsFragmentDoc } from './TranslatedStringSpecs';
export type RouterContextDataFragment = (
  { __typename: 'Router' }
  & Pick<SchemaTypes.Router, 'id' | 'hostname' | 'pathname' | 'companyId' | 'withAdmin'>
  & { company: (
    { __typename: 'Company' }
    & CompanyWithPermissionsContextDataFragment
  ), layout?: SchemaTypes.Maybe<(
    { __typename: 'BasicLayout' }
    & RouterLayoutContextData_BasicLayout_Fragment
  ) | (
    { __typename: 'BasicProLayout' }
    & RouterLayoutContextData_BasicProLayout_Fragment
  )>, endpoints: Array<(
    { __typename: 'ApplicationListEndpoint' }
    & EndpointContextData_ApplicationListEndpoint_Fragment
  ) | (
    { __typename: 'ConfiguratorApplicationEntrypoint' }
    & EndpointContextData_ConfiguratorApplicationEntrypoint_Fragment
  ) | (
    { __typename: 'CustomerListEndpoint' }
    & EndpointContextData_CustomerListEndpoint_Fragment
  ) | (
    { __typename: 'DummyPrivatePageEndpoint' }
    & EndpointContextData_DummyPrivatePageEndpoint_Fragment
  ) | (
    { __typename: 'DummyWelcomePageEndpoint' }
    & EndpointContextData_DummyWelcomePageEndpoint_Fragment
  ) | (
    { __typename: 'EventApplicationEntrypoint' }
    & EndpointContextData_EventApplicationEntrypoint_Fragment
  ) | (
    { __typename: 'FinderApplicationEntrypoint' }
    & EndpointContextData_FinderApplicationEntrypoint_Fragment
  ) | (
    { __typename: 'FinderApplicationPublicAccessEntrypoint' }
    & EndpointContextData_FinderApplicationPublicAccessEntrypoint_Fragment
  ) | (
    { __typename: 'MobilityApplicationEntrypoint' }
    & EndpointContextData_MobilityApplicationEntrypoint_Fragment
  ) | (
    { __typename: 'StandardApplicationEntrypoint' }
    & EndpointContextData_StandardApplicationEntrypoint_Fragment
  ) | (
    { __typename: 'StandardApplicationPublicAccessEntrypoint' }
    & EndpointContextData_StandardApplicationPublicAccessEntrypoint_Fragment
  ) | (
    { __typename: 'WebPageEndpoint' }
    & EndpointContextData_WebPageEndpoint_Fragment
  )>, languages: Array<(
    { __typename: 'LanguagePack' }
    & Pick<SchemaTypes.LanguagePack, 'id' | 'code' | 'displayName'>
  )>, menuItems: Array<(
    { __typename: 'MenuCustomPathItem' }
    & MenuItemSpecs_MenuCustomPathItem_Fragment
  ) | (
    { __typename: 'MenuEndpointItem' }
    & MenuItemSpecs_MenuEndpointItem_Fragment
  ) | (
    { __typename: 'MenuLogoutActionItem' }
    & MenuItemSpecs_MenuLogoutActionItem_Fragment
  )> }
);

export const RouterContextDataFragmentDoc = /*#__PURE__*/ gql`
    fragment RouterContextData on Router {
  id
  hostname
  pathname
  companyId
  company {
    ...CompanyWithPermissionsContextData
  }
  layout {
    ...RouterLayoutContextData
  }
  endpoints {
    ...EndpointContextData
  }
  languages {
    id
    code
    displayName
  }
  menuItems {
    ...MenuItemSpecs
  }
  withAdmin
}
    `;